import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostListener,
  Inject,
  InjectionToken,
  Input,
  LOCALE_ID,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';

import { DataAccessFairsFacade, FairConfiguration, FairsConfiguration } from '@planone/bau365/data-access-fair-data';
import { DataAccessScreensizeFacade } from '@planone/bau365/data-access-screensize';
import { DataAccessSearchPageFacade } from '@planone/bau365/data-access-search';
/* eslint @nrwl/nx/enforce-module-boundaries: 0 */
import { DataAccessUserFacade } from '@planone/bau365/data-access-user';
import { KeycloakLinkGenerator } from '@planone/bau365/util-keycloak';
import { BusinessCardImage } from '@planone/bau365/util-user-data';
import { DataAccessTogglesFacade } from '@planone/shared/data-access-feature-flags';
import { WindowRef } from '@planone/shared/util-browser-api';
import { KeycloakService } from 'keycloak-angular';
import { combineLatest, map, Observable, Subject, take, takeUntil, withLatestFrom } from 'rxjs';

export const SELFSERVICE_CONFIG = new InjectionToken<string>('SELFSERVICE_CONFIG');

@Component({
  selector: 'bau365-shell-layout',
  templateUrl: './layout.component.html',
  styleUrls: ['./layout.component.scss'],
})
export class LayoutComponent implements OnInit, OnDestroy {
  @Input()
  isLoggedIn = false;

  @Input()
  showFooter: boolean | null = true;

  @Output()
  logout: EventEmitter<void> = new EventEmitter<void>();

  givenName = '';
  familyName = '';
  initials = '';
  email = '';
  loginLink = '';
  selfServiceLink?: string;
  isFirstLevelMenuOpen = false;
  isSecondLevelMenuOpen = false;
  isThirdLevelMenuOpen = false;
  currentSecondLevelFairId?: string = '';
  currentThirdLevelFairId?: string = '';
  detailed = true;

  isSubmenuActive = false;

  fairId$: Observable<string>;
  fairsConfiguration$: Observable<FairsConfiguration>;
  allFairs$: Observable<FairConfiguration[]>;
  pastFairs$: Observable<FairConfiguration[]>;
  size$: Observable<string | undefined>;

  searchEnabled$: Observable<boolean | undefined>;
  programEnabled$: Observable<boolean | undefined>;
  watchlistEnabled$: Observable<boolean | undefined>;
  selfServiceEnabled$: Observable<boolean | undefined>;
  hallplanEnabled$: Observable<boolean | undefined>;
  showMobileBottomBar$: Observable<boolean | undefined>;
  qrCodeScannerEnabled$: Observable<boolean | undefined>;
  profileImage$: Observable<BusinessCardImage | undefined>;

  get isMenuOpen() {
    return this.isFirstLevelMenuOpen || this.isSecondLevelMenuOpen || this.isThirdLevelMenuOpen;
  }

  private unsubscribe: Subject<void> = new Subject<void>();

  constructor(
    public togglesFacade: DataAccessTogglesFacade,
    private searchPageFacade: DataAccessSearchPageFacade,
    private screensizeFacade: DataAccessScreensizeFacade,
    private fairsFacade: DataAccessFairsFacade,
    private userFacade: DataAccessUserFacade,
    private keycloakService: KeycloakService,
    private keycloakLinkGenerator: KeycloakLinkGenerator,
    private router: Router,
    private changeDetectorRef: ChangeDetectorRef,
    private window: WindowRef,
    @Inject(LOCALE_ID) private localeId: string,
    @Inject(SELFSERVICE_CONFIG) private selfserviceHost: string
  ) {
    this.searchEnabled$ = this.togglesFacade.flag('kill-search').pipe(map((searchDisabled) => !searchDisabled));
    this.programEnabled$ = this.togglesFacade.flag('kill-program').pipe(map((programDisabled) => !programDisabled));
    this.watchlistEnabled$ = this.togglesFacade
      .flag('kill-watchlist')
      .pipe(map((watchlistDisabled) => !watchlistDisabled));
    this.hallplanEnabled$ = this.togglesFacade
      .flag('kill-hall-plan')
      .pipe(map((hallplanDisabled) => !hallplanDisabled));
    this.showMobileBottomBar$ = this.togglesFacade.flag('show-mobile-bottom-bar');
    this.selfServiceEnabled$ = this.togglesFacade.flag('show-self-service');
    this.qrCodeScannerEnabled$ = this.togglesFacade
      .flag('kill-qrcode-scanner')
      .pipe(map((qrCodeScannerDisabled) => !qrCodeScannerDisabled));

    router.events.pipe(takeUntil(this.unsubscribe)).subscribe((change) => {
      this.detailed = !(change instanceof NavigationEnd && change.url === '/profilecompletion');
      this.isSubmenuActive = this.router.url.includes('/fairs');

      this.changeDetectorRef.detectChanges();
    });

    this.fairId$ = fairsFacade.fairIdFromRoute$;
    this.fairsConfiguration$ = fairsFacade.fairsConfig$;
    this.size$ = screensizeFacade.size$;

    this.allFairs$ = this.fairsConfiguration$.pipe(
      map((fairsConfig) => Object.entries(fairsConfig.fairs).map(([key, value]) => ({ ...value, id: key })))
    );

    this.pastFairs$ = this.allFairs$.pipe(
      withLatestFrom(this.fairsConfiguration$),
      map(([fairs, fairsConfiguration]) =>
        fairs
          .filter((fair) => fair.id !== fairsConfiguration.activeFair && fair.id !== fairsConfiguration.bauInsights)
          .sort((value) => value.startDate?.getTime() ?? -1)
          .reverse()
      )
    );

    this.profileImage$ = this.userFacade.getUserProfileImage$;
  }

  ngOnInit() {
    const instance = this.keycloakService.getKeycloakInstance();
    const token = instance.tokenParsed;
    const locale = this.localeId;

    this.fairId$.pipe(take(1)).subscribe((fairId) => {
      this.loginLink = this.keycloakLinkGenerator.createLoginUrl({ locale, fairId });

      if (token) {
        this.givenName = token['given_name'];
        this.familyName = token['family_name'];
        this.initials = token['given_name'].charAt(0).toUpperCase() + token['family_name'].charAt(0).toUpperCase();
        this.email = token['email'];
        this.selfServiceLink = undefined;

        if ((token.realm_access?.roles ?? []).includes('self-service')) {
          this.selfServiceLink = this.selfserviceHost;
        }
      }
    });

    this.screensizeFacade.screenResized(this.window.nativeWindow.innerWidth);
    this.fairsFacade.appOpened();
  }

  ngOnDestroy() {
    this.unsubscribe.next();
    this.unsubscribe.complete();
  }

  openOrCloseMenu(): void {
    this.isFirstLevelMenuOpen = !this.isFirstLevelMenuOpen;
    this.searchPageFacade.overlayToggled(this.isFirstLevelMenuOpen);
  }

  closeMenu(): void {
    this.isFirstLevelMenuOpen = false;
    this.isSecondLevelMenuOpen = false;
    this.isThirdLevelMenuOpen = false;
    this.searchPageFacade.overlayToggled(this.isFirstLevelMenuOpen);
  }

  doLogout() {
    this.logout.next();
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: { target: { innerWidth: number } }) {
    this.screensizeFacade.screenResized(event.target.innerWidth);
  }

  openSubMenu(fairId?: string, pastFairDetailedLevel?: boolean) {
    if (pastFairDetailedLevel) {
      this.currentThirdLevelFairId = fairId;
      this.isThirdLevelMenuOpen = true;
    } else {
      this.currentSecondLevelFairId = fairId;
      this.isSecondLevelMenuOpen = true;
    }
  }

  closeSubMenu(pastFairDetailedLevel?: boolean) {
    pastFairDetailedLevel ? (this.isThirdLevelMenuOpen = false) : (this.isSecondLevelMenuOpen = false);
  }

  isMenuItemActive(route: string[]): boolean {
    const urlTree = this.router.createUrlTree(route);
    return this.router.isActive(urlTree, {
      matrixParams: 'ignored',
      queryParams: 'ignored',
      paths: 'subset',
      fragment: 'ignored',
    });
  }

  isAllFairsMenuItemActive(route: string[]): Observable<boolean> {
    return combineLatest([this.fairId$, this.fairsConfiguration$]).pipe(
      map(([fairId, fairsConfiguration]) => {
        const excludedFromCheck = [fairsConfiguration.activeFair, fairsConfiguration.bauInsights];
        if (fairId && excludedFromCheck.includes(fairId)) {
          return false;
        }
        const urlTree = this.router.createUrlTree(route);
        return this.router.isActive(urlTree, {
          matrixParams: 'ignored',
          queryParams: 'ignored',
          paths: 'subset',
          fragment: 'ignored',
        });
      })
    );
  }

  identifyFairConfig(_index: number, fairConfig: FairConfiguration): string {
    return fairConfig.id ?? fairConfig.name;
  }
}
